import React, { useState, useEffect, useCallback } from "react";
import { customFetch } from "../apiClient";
import UsgReceiptPrint from "../components/UsgReceiptPrint";
import QuillEditor from "../components/QuillEditor";

// Utility: Format Date for the table
function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export default function UsgReportHistory() {
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filters & Pagination States
  const [useDateFilter, setUseDateFilter] = useState(true);
  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modals Data
  const [companyDetails, setCompanyDetails] = useState(null);
  
  // Print Modal
  const [showPrint, setShowPrint] = useState(false);
  const [printData, setPrintData] = useState(null);

  // Edit Modal
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  // 1. Fetch Company Details
  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => setCompanyDetails(d))
        .catch(console.error);
    }
  }, []);

  // Auto-dismiss alerts
  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); } }, [error]);

  // 2. Fetch History API
  const fetchHistory = useCallback(async (page = currentPage, rows = rowsPerPage) => {
    setLoading(true);
    setError("");
    try {
      let url = `/UsgDt?page=${page}&pageSize=${rows}&useDateFilter=${useDateFilter}`;
      if (useDateFilter) {
        url += `&from=${fromDate}&to=${toDate}`;
      }
      if (searchText) {
        url += `&search=${encodeURIComponent(searchText)}`;
      }

      const res = await customFetch(url);
      if (!res.ok) throw new Error("Failed to fetch report history");
      
      const responseData = await res.json();
      
      // Handle the PagedResult DTO structure
      setHistoryList(responseData.data || []);
      setTotalRecords(responseData.totalRecords || 0);
      setCurrentPage(page);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [useDateFilter, fromDate, toDate, searchText, currentPage, rowsPerPage]);

  useEffect(() => {
    fetchHistory(1, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDateFilter, fromDate, toDate, searchText]); // Reload when filters change

  const handlePageChange = (page) => {
    fetchHistory(page, rowsPerPage);
  };

  // 3. Actions
  const handlePrint = (record) => {
    setPrintData(record);
    setShowPrint(true);
  };

  const handleEditClick = (record) => {
    // Clone the record to avoid direct state mutation
    setEditData({ ...record });
    setShowEdit(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this report?")) return;
    
    setLoading(true);
    try {
      const res = await customFetch(`/UsgDt/${id}`, { method: "DELETE" });
      if (res.ok) {
        setSuccess("Report deleted successfully");
        fetchHistory(currentPage, rowsPerPage);
      } else {
        setError("Failed to delete report");
      }
    } catch (err) {
      setError("Network error while deleting");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRecord = async () => {
    if (!editData) return;
    setSaving(true);
    setError("");

    try {
      const res = await customFetch(`/UsgDt/${editData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });

      if (res.ok || res.status === 204) {
        setSuccess("Report updated successfully!");
        setShowEdit(false);
        setEditData(null);
        fetchHistory(currentPage, rowsPerPage);
      } else {
        const msg = await res.text();
        setError(`Failed to update: ${msg}`);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Pagination Logic
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const renderPagination = () => {
    if (totalRecords === 0) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button key={i} className={`pagination-btn ${currentPage === i ? 'active' : ''}`} onClick={() => handlePageChange(i)}>
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="advance-form-container compact-ui">
      <div className="advance-card">
        
        {/* ── Header ── */}
        <div className="advance-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-clock-history"></i></div>
            <div className="header-text">
              <h5>Diagnostic Report History</h5>
              <p>Search, View, Print, and Edit past USG & Lab reports</p>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <i className="bi bi-files"></i>
              <div className="stat-info">
                <span className="stat-value">{totalRecords}</span>
                <span className="stat-label">Total Reports</span>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x"></i></button>
          </div>
        )}

        <div className="main-content">
          
          {/* ── Filters ── */}
          <div className="filters-section">
            <div className="section-header">
              <i className="bi bi-funnel"></i>
              <span>Filters & Search</span>
            </div>
            <div className="filters-content">
              
              <div className="date-check-row">
                <input 
                  type="checkbox" 
                  id="dateFilterCheck"
                  checked={useDateFilter} 
                  onChange={(e) => setUseDateFilter(e.target.checked)} 
                />
                <label htmlFor="dateFilterCheck">Apply Date Filter</label>
              </div>

              <div className="filter-row">
                <div className="filter-group">
                  <label><i className="bi bi-calendar-event"></i> From Date</label>
                  <input type="date" className="form-control-custom" value={fromDate} onChange={(e) => setFromDate(e.target.value)} disabled={!useDateFilter} style={{ opacity: !useDateFilter ? 0.5 : 1 }} />
                </div>

                <div className="filter-group">
                  <label><i className="bi bi-calendar-check"></i> To Date</label>
                  <input type="date" className="form-control-custom" value={toDate} onChange={(e) => setToDate(e.target.value)} disabled={!useDateFilter} style={{ opacity: !useDateFilter ? 0.5 : 1 }} />
                </div>

                <div className="filter-group filter-group-flex2">
                  <label><i className="bi bi-search"></i> Search Patient / UHID</label>
                  <input
                    type="text" className="form-control-custom" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search by Name, UHID, or Mobile..."
                  />
                </div>

                <div className="filter-group filter-group-rows">
                  <label><i className="bi bi-table"></i> Rows</label>
                  <input type="number" className="form-control-custom" value={rowsPerPage} min="1" onChange={(e) => { setRowsPerPage(Number(e.target.value) || 10); setCurrentPage(1); }} />
                </div>

                <div className="filter-group filter-group-btn">
                  <label>&nbsp;</label>
                  <button className="btn-refresh" onClick={() => fetchHistory(1)} disabled={loading}>
                    <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i> Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div className="table-container">
            {loading && historyList.length === 0 ? (
              <div className="empty-state-small">
                <div className="spinner-border text-primary" role="status" style={{width: '24px', height: '24px'}}></div>
                <p>Loading History...</p>
              </div>
            ) : historyList.length === 0 ? (
              <div className="empty-state-small">
                <i className="bi bi-inbox"></i>
                <p>No reports found matching your criteria.</p>
              </div>
            ) : (
              <table className="records-table">
                <thead>
                  <tr>
                    <th style={{ width: '70px' }} className="text-center">Rep No</th>
                    <th style={{ width: '90px' }}>Date</th>
                    <th style={{ width: '110px' }}>UHID</th>
                    <th style={{ width: '180px' }}>Patient Name</th>
                    <th style={{ width: '110px' }}>Mobile</th>
                    <th style={{ width: '180px' }}>Test Name</th>
                    <th>Doctor</th>
                    <th style={{ width: '90px' }} className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {historyList.map((row) => (
                    <tr key={row.id}>
                      <td className="text-center">
                        <span className="amount-badge" style={{background: '#e0f2fe', color: '#0284c7'}}>{row.hid_SrNo || row.id}</span>
                      </td>
                      <td><span className="date-text"><i className="bi bi-calendar"></i> {formatDateToDDMMYYYY(row.reportDate)}</span></td>
                      <td><span className="text-muted fw-bold">{row.uhid_No || row.uhiD_No || "-"}</span></td>
                      <td><strong>{row.patientName}</strong></td>
                      <td><span className="date-text"><i className="bi bi-telephone"></i> {row.mobileNo}</span></td>
                      <td><span className="remarks-text" style={{ fontStyle: 'normal', color: '#0f172a' }}>{row.testName}</span></td>
                      <td><span className="text-muted"><i className="bi bi-person-fill-check me-1"></i> {row.doctorName || "N/A"}</span></td>
                      <td className="text-center">
                        <div className="action-buttons">
                          <button className="btn-icon btn-preview" onClick={() => handlePrint(row)} title="View / Print">
                            <i className="bi bi-printer"></i>
                          </button>
                          <button className="btn-icon btn-edit" onClick={() => handleEditClick(row)} title="Edit Content">
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          <button className="btn-icon btn-delete" onClick={() => handleDelete(row.id)} title="Delete">
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="pagination-section">
              {renderPagination()}
            </div>
          )}

        </div>
      </div>

      {/* ── Print Modal ── */}
      {showPrint && printData && companyDetails && (
        <UsgReceiptPrint
          show={showPrint}
          data={printData}
          companyDetails={companyDetails}
          onClose={() => { setShowPrint(false); setPrintData(null); }}
        />
      )}

      {/* ── Edit Modal ── */}
      {showEdit && editData && (
        <div className="modal-backdrop">
          <div className="convert-modal slide-in" style={{ maxWidth: '850px', width: '95%' }}>
            <div className="modal-header">
              <h6><i className="bi bi-pencil-square me-2"></i> Edit Report Content</h6>
              <button className="close-btn" onClick={() => setShowEdit(false)}><i className="bi bi-x-lg"></i></button>
            </div>
            
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <div className="patient-summary mb-3">
                 <h6>{editData.patientName} <span className="text-muted ms-2 fw-normal" style={{fontSize: '11px'}}>(UHID: {editData.uhid_No})</span></h6>
                 <div className="details">
                    <span><i className="bi bi-file-medical text-primary"></i> <strong>Test:</strong> {editData.testName}</span>
                    <span><i className="bi bi-calendar text-primary"></i> <strong>Date:</strong> {formatDateToDDMMYYYY(editData.reportDate)}</span>
                 </div>
              </div>

              <div className="form-group mb-3">
                <label>Test Name</label>
                <input 
                  type="text" 
                  className="form-control-custom" 
                  value={editData.testName} 
                  onChange={(e) => setEditData({...editData, testName: e.target.value})} 
                />
              </div>

              <div className="form-group mb-1">
                <label>Report Content</label>
              </div>
              <div style={{ border: '1px solid #cbd5e0', borderRadius: '6px', overflow: 'hidden' }}>
                <QuillEditor
                  value={editData.testContent}
                  onChange={(content) => setEditData({...editData, testContent: content})}
                  minHeight="350px"
                />
              </div>

              <div className="form-group mt-3">
                <label>Remarks</label>
                <input 
                  type="text" 
                  className="form-control-custom" 
                  value={editData.remarks || ""} 
                  onChange={(e) => setEditData({...editData, remarks: e.target.value})} 
                  placeholder="Internal notes/remarks..."
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-cancel-modal" onClick={() => setShowEdit(false)}>Cancel</button>
              <button className="btn-confirm-modal" onClick={handleUpdateRecord} disabled={saving}>
                 {saving ? <><span className="spinner-border spinner-border-sm me-2"></span> Updating...</> : <><i className="bi bi-check-circle me-1"></i> Update Report</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Match CSS from OpdList / AdvanceForm ── */}
      <style>{`
        .compact-ui { font-family: system-ui, -apple-system, sans-serif; }
        .advance-form-container { padding: 0; max-height: 100vh; overflow-y: auto; background: #f8fafc; min-height: 100vh; padding-bottom: 40px; }
        .advance-card { background: white; border-radius: 8px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05); border: 1px solid #e0e6ed; overflow: visible; margin: 15px; }
        
        .advance-header { background: var(--theme-color, #0088cc); padding: 10px 16px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #00d4aa; border-top-left-radius: 8px; border-top-right-radius: 8px; }
        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon { width: 34px; height: 34px; background: rgba(255, 255, 255, 0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 16px; color: white; }
        .header-text h5 { margin: 0; color: white; font-size: 15px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10.5px; }

        .header-stats { display: flex; gap: 12px; }
        .stat-item { background: rgba(255,255,255,0.2); padding: 4px 10px; border-radius: 6px; display: flex; align-items: center; gap: 6px; }
        .stat-item i { font-size: 16px; color: white; }
        .stat-info { display: flex; flex-direction: column; }
        .stat-value { color: white; font-size: 14px; font-weight: 700; line-height: 1; }
        .stat-label { color: rgba(255,255,255,0.85); font-size: 9px; }

        .main-content { display: flex; flex-direction: column; gap: 12px; padding: 16px; }

        .filters-section { background: #ffffff; border: 1px solid #e0e6ed; border-radius: 8px; overflow: visible; }
        .section-header { background: #f0f4f8; padding: 8px 12px; display: flex; align-items: center; gap: 6px; border-bottom: 2px solid var(--theme-color, #0088cc); border-top-left-radius: 8px; border-top-right-radius: 8px; }
        .section-header i { font-size: 13px; color: var(--theme-color, #0088cc); }
        .section-header span { font-size: 12px; font-weight: 600; color: #2c3e50; flex: 1; }
        .filters-content { padding: 12px; }
        
        .date-check-row { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
        .date-check-row input[type="checkbox"] { width: 14px; height: 14px; cursor: pointer; }
        .date-check-row label { font-size: 11px; font-weight: 600; color: #2c3e50; cursor: pointer; margin: 0; }

        .filter-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: end; }
        .filter-group { display: flex; flex-direction: column; flex: 1 1 120px; min-width: 100px; }
        .filter-group-flex2 { flex: 2 1 180px; min-width: 150px; }
        .filter-group-rows { flex: 0 1 70px; min-width: 60px; }
        .filter-group-btn  { flex: 0 1 80px; min-width: 70px; }
        
        .filter-group label { font-size: 10px; font-weight: 600; color: #495057; margin-bottom: 3px; display: flex; align-items: center; gap: 3px; }
        .filter-group label i { color: var(--theme-color, #0088cc); font-size: 11px; }

        .form-control-custom { padding: 3px 7px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; width: 100%; height: 28px; outline: none; transition: 0.2s; }
        .form-control-custom:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,0,0,0.05); }

        .btn-refresh { padding: 0 10px; border-radius: 4px; border: none; background: var(--theme-color, #0088cc); color: white; font-size: 11px; font-weight: 600; cursor: pointer; height: 28px; display: flex; align-items: center; justify-content: center; gap: 4px; width: 100%; }
        
        /* Table Styles */
        .table-container { background: white; border: 1px solid #e0e6ed; border-radius: 7px; width: 100%; overflow-x: auto; }
        .records-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px; min-width: 800px; }
        .records-table thead tr { background: var(--theme-color, #0088cc); }
        .records-table thead th { padding: 8px 10px; text-align: left; font-weight: 600; color: white; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; border: none; }
        .records-table tbody tr { background: white; border-bottom: 1px solid #e9ecef; transition: all 0.2s; }
        .records-table tbody tr:hover { background: #f8f9fa; }
        .records-table tbody td { padding: 8px 10px; border-bottom: 1px solid #e9ecef; vertical-align: middle; }
        
        .empty-state-small { text-align: center; padding: 2.5rem 1rem; color: #9ca3af; display: flex; flex-direction: column; align-items: center; gap: 10px;}
        .empty-state-small i { font-size: 2.5rem; margin-bottom: 0.3rem; display: block; }

        .amount-badge { display: inline-flex; align-items: center; gap: 0.2rem; background: #dcfce7; color: #166534; padding: 0.2rem 0.6rem; border-radius: 4px; font-weight: 700; font-size: 11px; }
        .date-text { display: inline-flex; align-items: center; gap: 0.25rem; color: #4b5563; font-size: 11px; white-space: nowrap; }
        .date-text i { color: var(--theme-color, #0088cc); }

        /* Action Buttons */
        .action-buttons { display: flex; gap: 0.4rem; justify-content: center; }
        .btn-icon { width: 26px; height: 26px; border: none; border-radius: 4px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 12px; }
        .btn-preview { background: #e0f2fe; color: #0369a1; }
        .btn-preview:hover { background: #0369a1; color: white; }
        .btn-edit { background: #dbeafe; color: #1e40af; }
        .btn-edit:hover { background: #1e40af; color: white; }
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; }

        /* Pagination */
        .pagination-section { display: flex; justify-content: center; gap: 5px; margin-top: 8px; flex-wrap: wrap; }
        .pagination-btn { padding: 3px 9px; border-radius: 4px; border: 1px solid #cbd5e0; background: white; color: #2c3e50; font-size: 11px; font-weight: 600; cursor: pointer; }
        .pagination-btn.active { background: var(--theme-color, #0088cc); color: white; border-color: var(--theme-color, #0088cc); }
        .pagination-btn:hover:not(.active) { background: #f0f4f8; }

        /* Modals */
        .modal-backdrop { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.6); display: flex; justify-content: center; align-items: center; z-index: 1050; backdrop-filter: blur(3px); }
        .convert-modal { background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2); display: flex; flex-direction: column; overflow: hidden;}
        .convert-modal .modal-header { background: var(--theme-color, #0088cc); padding: 15px 20px; color: white; display: flex; justify-content: space-between; align-items: center; }
        .convert-modal .modal-header h6 { margin: 0; font-size: 15px; font-weight: 600; }
        .close-btn { background: transparent; border: none; color: white; font-size: 18px; cursor: pointer; opacity: 0.8; }
        
        .convert-modal .modal-body { padding: 20px; }
        .patient-summary { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; }
        .patient-summary h6 { color: var(--theme-color, #0088cc); font-size: 14px; margin-bottom: 8px; font-weight: 700; }
        .patient-summary .details { display: flex; flex-wrap: wrap; gap: 15px; font-size: 12px; color: #475569; }
        
        .convert-modal .modal-footer { padding: 15px 20px; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 10px; }
        .btn-cancel-modal { background: #e2e8f0; color: #475569; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; }
        .btn-confirm-modal { background: var(--theme-color, #0088cc); color: white; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; }

        @keyframes slideIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        .slide-in { animation: slideIn 0.3s ease; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinning { animation: spin 0.8s linear infinite; display:inline-block; }

        .alert-custom { display: flex; align-items: center; gap: 8px; padding: 7px 14px; margin: 10px 14px 0; border-radius: 6px; font-size: 11px; }
        .alert-danger { background: #fff5f5; color: #d63031; border: 1px solid #ffb8b8; }
        .alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
        .alert-close { margin-left: auto; background: none; border: none; cursor: pointer; opacity: 0.6; }
      `}</style>
    </div>
  );
}